import { useEffect } from "react";
import moment from "moment";

import { SiteCreateState } from "@/Administration/Components/Sites/SiteCreate";
import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { DayOfWeek } from "@/Common/Models/DayOfWeek";
import { LocalizedTextDto } from "@/Common/Models/LocalizedTextDto";
import { OpeningHour } from "@/Common/Models/Sites/OpeningHour";

import styles from "@/Administration/Components/Sites/SiteInfoInPortal.module.scss";

interface IOpeningHoursSubSectionProps {
    openingHours: OpeningHour[];
    localizedOpeningHoursNote: LocalizedTextDto;
    mutateState: (mutation: (state: SiteCreateState) => void, then?: () => void) => void;
}

function OpeningHoursSubSection(props: IOpeningHoursSubSectionProps): JSX.Element {
    const { openingHours, localizedOpeningHoursNote, mutateState } = props;
    const timeFormat = "HH:mm:ss";
    const initialOpeningTime = "08:00:00";
    const initialClosingTime = "17:00:00";
    const siteOpeningHours: OpeningHour[] = EnumHelper
        .getValues(DayOfWeek)
        .map(dayOfWeek => {
            if (openingHours?.any()) {
                const openingHour = openingHours.find(item => item.DayOfWeek === dayOfWeek);

                return {
                    DayOfWeek: openingHour?.DayOfWeek || dayOfWeek,
                    OpeningTime: openingHour?.OpeningTime ? openingHour.OpeningTime : null,
                    ClosingTime: openingHour?.ClosingTime ? openingHour.ClosingTime : null
                };
            }
            else {
                const isWeekday = dayOfWeek !== DayOfWeek.Saturday && dayOfWeek !== DayOfWeek.Sunday;

                return {
                    DayOfWeek: dayOfWeek,
                    OpeningTime: isWeekday ? initialOpeningTime : null,
                    ClosingTime: isWeekday ? initialClosingTime : null
                };
            }
        });
    const newSiteOpeningHours = [...siteOpeningHours?.slice(1), siteOpeningHours[0]];

    const onNoteChangeHandle = (value: string, propertyName: string): void => {
        mutateState(s => s.localizedOpeningHoursNote = {
            ...localizedOpeningHoursNote,
            [propertyName]: value
        });
    };

    const getFilteredOpeningHours = (openingHours: OpeningHour[]): OpeningHour[] => openingHours.filter(item => item.OpeningTime && item.ClosingTime);

    const onChangeOpeningHourCheckbox = (index: number, checked: boolean): void => {
        if (!checked) {
            newSiteOpeningHours[index].OpeningTime = null;
            newSiteOpeningHours[index].ClosingTime = null;
        }
        else {
            newSiteOpeningHours[index].OpeningTime = openingHours[index]?.OpeningTime || initialOpeningTime;
            newSiteOpeningHours[index].ClosingTime = openingHours[index]?.ClosingTime || initialClosingTime;
        }
        mutateState(s => s.openingHours = getFilteredOpeningHours(newSiteOpeningHours));
    };

    const onOpeningTimeChange = (index: number, value: string): void => {
        // Moment format used here, because Json.NET works only with HH:mm:ss
        newSiteOpeningHours[index].OpeningTime = moment(value, timeFormat).format(timeFormat);
        mutateState(s => s.openingHours = getFilteredOpeningHours(newSiteOpeningHours));
    };

    const onClosingTimeChange = (index: number, value: string): void => {
        // Moment format used here, because Json.NET works only with HH:mm:ss
        newSiteOpeningHours[index].ClosingTime = moment(value, timeFormat).format(timeFormat);
        mutateState(s => s.openingHours = getFilteredOpeningHours(newSiteOpeningHours));
    };

    useEffect(() => {
        mutateState(s => s.openingHours = getFilteredOpeningHours(newSiteOpeningHours));
    }, []);

    return (
        <>
            <div className="view-subsection-header row">
                <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                    <h4 className="view-subsection-title">{cacheStore.GetString("ClinicInfo_OpeningHoursLabel")}</h4>
                </div>
            </div>

            <div className="view-subsection-body">
                {newSiteOpeningHours.map((openingHour, index) => {
                    const isClosed = !(openingHour?.OpeningTime && openingHour?.ClosingTime);

                    return (
                        <div key={`hours_${index}`} className="row" style={{ marginBottom: "15px" }}>
                            <div className="col-lg-4 col-md-2 col-sm-2 text-right margin-xs-top">
                                <div className={styles.openingHourCheckbox}>
                                    <CheckboxControl
                                        checked={!isClosed}
                                        label={AdministrationHelper.getDayOfWeekLabel(openingHour.DayOfWeek)}
                                        onChange={e => onChangeOpeningHourCheckbox(index, e)}
                                        item={openingHour}
                                    />
                                </div>
                            </div>
                            <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                                {isClosed ? (
                                    <label className="form-label margin-xs-top">
                                        {cacheStore.GetString("ClinicInfo_SiteClosedLabel")}
                                    </label>
                                )
                                    : (
                                        <div className={styles.openingHoursGrid}>
                                            <input
                                                type="time"
                                                className="form-control"
                                                id={`opening_time_${index}`}
                                                value={openingHour.OpeningTime}
                                                onChange={e => onOpeningTimeChange(index, e.target.value)}
                                            />
                                            <span>{cacheStore.GetString("ToLabel").toLowerCase()}</span>
                                            <input
                                                type="time"
                                                className="form-control"
                                                id={`closing_time_${index}`}
                                                value={openingHour.ClosingTime}
                                                onChange={e => onClosingTimeChange(index, e.target.value)}
                                            />
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                    );
                })}

                {AdministrationHelper.getLocalizedTextSiteCreateArray(localizedOpeningHoursNote).map((item, index) => (
                    <div key={`notes_${index}`} className="row" style={{ marginBottom: "15px" }}>
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{`${cacheStore.GetString("NotesLabel")} (${item.languageName})`}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <textarea
                                className="form-control"
                                value={item.value}
                                maxLength={AdministrationHelper.maxNoteLengthLimit}
                                onChange={e => {
                                    if (e.target.value.length > AdministrationHelper.maxNoteLengthLimit) {
                                        return;
                                    }
                                    onNoteChangeHandle(e.target.value, item.propertyName);
                                }}
                            />
                            <label data-cy="counter-label">
                                {`${item.value?.length ?? 0}/${AdministrationHelper.maxNoteLengthLimit}`}
                            </label>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

export { IOpeningHoursSubSectionProps };
export default OpeningHoursSubSection;
